/**
 * EVERY ANSWERABLE QUESTION OPENS ALREADY ANSWERED "No" — **D-016**.
 *
 * The design authority, walkthrough round 3, 2026-08-06:
 *
 *   *"All answers should default to No (quantity 0) until the user selects Yes,
 *    at which point they must enter a quantity (if applicable). Many questions
 *    are not defaulted. I just closed and submitted an incomplete survey, and
 *    now there's no way to recall or delete it. I should not have been able to
 *    submit with unanswered questions. This wouldn't be a big deal if the
 *    questions had been defaulted to No from the start."*
 *
 * ── The distinction this module exists to get right ──────────────────────────
 * Round 2 made the CONTROL show No. That is not the same as the DOCUMENT saying
 * No, and the difference is the whole of the report above: a section walked
 * without touching anything looked complete on screen and arrived at the office
 * as sixteen unanswered questions per room. So the default is a real write —
 * `doc.answers[key]` genuinely holds `No` — and the surveyor can walk a section
 * touching only what applies.
 *
 * ── Four properties, and each one is load-bearing ────────────────────────────
 *
 *  1. **It never overwrites.** A key that already carries ANY value is left
 *     alone. The test is raw presence in `answers`, deliberately not
 *     `isAnswered()`: a `flagCount` sitting at *"Yes, 0 of them"* reads as
 *     unanswered (that contradiction is a gap, see validation.ts) and must NOT
 *     be quietly reset to No underneath the surveyor's thumb.
 *  2. **It reaches questions revealed later.** The pass runs over whatever
 *     `QuestionList` currently has on screen, so a gate opened mid-section
 *     defaults its newly-revealed children on the same render that reveals them.
 *  3. **It is idempotent.** Running it twice writes nothing the second time, and
 *     the presence check is repeated INSIDE the store transaction, so two
 *     screens racing cannot double-write.
 *  4. **It never touches a sealed document.** A submitted survey is the office's
 *     record (brief §4). Nothing here may add an answer to one, ever.
 *
 * ── Provenance, and why it has to exist ──────────────────────────────────────
 * A written default is indistinguishable from a surveyor's answer by value, so
 * every key this module writes is recorded in `SurveyDocument.defaultedAnswers`
 * and removed from it the moment the surveyor writes that answer themselves.
 * Exactly one thing consults it — the D-014/D-015 timing gate — and without it a
 * six-car room's Electrical section would open at 0 + 0 ≠ 6 and be red before
 * the surveyor had touched anything, which is the defect D-015 was raised about.
 * See `validationTiming.ts` and the note on `SurveyDocument.defaultedAnswers`.
 *
 * Pure and React-free: documents in, a plan out, a mutator that takes a document.
 * `SurveyProvider` owns the write; nothing here reaches a store.
 */

import { byId, defaultAnswerFor } from '../../core/catalog';
import { isVisible } from '../../core/validation';
import {
  isSubmittedStatus,
  type AnswerKey,
  type AnswerValue,
  type QuestionDef,
  type Scope,
  type SurveyDocument,
} from '../../core/types';
import { keyFor } from './results';

/** One answer the form is about to fill in on the surveyor's behalf. */
export interface PendingDefault {
  /** The scoped key, narrowed exactly the way every other write narrows it. */
  key: AnswerKey;
  questionId: string;
  value: AnswerValue;
}

/**
 * The defaults missing from a set of on-screen questions.
 *
 * Takes the questions rather than a section so the caller passes the list it is
 * actually RENDERING — visibility has already been applied by `visibleQuestions`
 * and must not be decided a second time here, or a question could be defaulted
 * while hidden and hidden while defaulted.
 */
export function pendingDefaults(
  doc: SurveyDocument,
  questions: readonly QuestionDef[],
  scope: Scope = {},
): PendingDefault[] {
  if (isSubmittedStatus(doc.status)) return [];

  const pending: PendingDefault[] = [];
  const claimed = new Set<AnswerKey>();

  for (const question of questions) {
    const value = defaultAnswerFor(question);
    if (!value) continue;

    const key = keyFor(question.id, scope);
    // Raw presence, not `isAnswered`. See property 1 in the module header.
    if (key in doc.answers) continue;
    if (claimed.has(key)) continue;

    claimed.add(key);
    pending.push({ key, questionId: question.id, value });
  }

  return pending;
}

/**
 * Apply a plan to a document, in place. Returns how many answers were written.
 *
 * Safe to hand straight to `store.updateDocument`: the guards are re-checked
 * here because the document inside the transaction is not the one the plan was
 * computed against — a write from another tab could have landed in between.
 */
export function applyAnswerDefaults(
  doc: SurveyDocument,
  pending: readonly PendingDefault[],
): number {
  if (pending.length === 0) return 0;
  if (isSubmittedStatus(doc.status)) return 0;

  const defaulted = new Set(doc.defaultedAnswers ?? []);
  let written = 0;

  for (const entry of pending) {
    if (entry.key in doc.answers) continue;
    doc.answers[entry.key] = entry.value;
    defaulted.add(entry.key);
    written += 1;
  }

  if (written > 0) doc.defaultedAnswers = [...defaulted].sort();
  return written;
}

/**
 * Is this answer the form's default rather than the surveyor's answer?
 *
 * The question this asks is *"has the surveyor engaged with this?"*, so a key
 * with no answer at all is NOT defaulted — it is unanswered, which the callers
 * already handle and must keep handling separately.
 */
export function isDefaultedKey(doc: SurveyDocument, key: AnswerKey): boolean {
  const marks = doc.defaultedAnswers;
  if (!marks || marks.length === 0) return false;
  return marks.includes(key);
}

/** The same question, by id and scope. */
export function isDefaultedAnswer(
  doc: SurveyDocument,
  questionId: string,
  scope: Scope = {},
): boolean {
  return isDefaultedKey(doc, keyFor(questionId, scope));
}

/**
 * Forget that a key was defaulted — the surveyor has answered it themselves.
 *
 * Called on EVERY surveyor write, including one that stores the same value the
 * default held: tapping "No" on a question already showing No is the surveyor
 * saying No, and that is a different document from the form having guessed.
 * Mutates in place, for use inside a store transaction.
 */
export function clearDefaultMark(doc: SurveyDocument, key: AnswerKey): void {
  const marks = doc.defaultedAnswers;
  if (!marks || marks.length === 0) return;
  const next = marks.filter((candidate) => candidate !== key);
  if (next.length === marks.length) return;
  if (next.length === 0) delete doc.defaultedAnswers;
  else doc.defaultedAnswers = next;
}

/**
 * A surveyor's answer, as `SurveyProvider.setAnswer` writes it — **D-055**.
 *
 * Read against the document the write LANDS in: a question that document no
 * longer asks (its parent went No, its room changed form set) takes nothing.
 * The case it exists for: a typed number in a gated child (G9a, G14a, A3a,
 * A2) is committed when its stepper unmounts without a blur (iOS — D-053),
 * and the parent's No is what unmounted it; by then the No is stored, so the
 * child is not asked, and its number must not be written under "no fence".
 * Every write the phone makes to a question is rendered from `visibleQuestions`
 * first, so this only ever drops a write whose question stopped being asked in
 * between — a write while it is asked (blur, Enter, a −/+ tap, leaving the
 * screen with the keyboard up) lands as before. An id the catalog does not
 * know is written as it always was. Returns whether it wrote; mutates in
 * place, for use inside a store transaction.
 */
export function applySurveyorAnswer(
  doc: SurveyDocument,
  questionId: string,
  scope: Scope,
  value: AnswerValue,
): boolean {
  const question = byId(questionId);
  if (question && !isVisible(question, doc, scope)) return false;
  const key = keyFor(questionId, scope);
  doc.answers[key] = value;
  clearDefaultMark(doc, key);
  return true;
}
